import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { WizardState, SelectedQuestion } from '../Index'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Eye, Sparkles, RefreshCw, Loader2, CheckCircle2, AlertCircle,
  SlidersHorizontal, Trash2, Replace, BarChart2, Check, ShieldCheck,
  BookOpen, Folder, ChevronDown, ChevronRight, ChevronsUpDown, ExternalLink
} from 'lucide-react'

import { KatexRenderer } from '@/components/shared/KatexRenderer'

interface Props {
  state: WizardState
  updateState: (patch: Partial<WizardState>) => void
}

interface QuestionItem {
  question: SelectedQuestion
  globalIndex: number
}

interface ChapterGroup {
  chapterId?: number
  chapterName: string
  totalQuestions: number
  totalMarks: number
  questions: QuestionItem[]
}

interface SubjectItem {
  subjectId?: number
  subjectName: string
  totalQuestions: number
  totalMarks: number
  chapters: ChapterGroup[]
}

interface PrimaryGroup {
  id?: number
  name: string
  totalQuestions: number
  totalMarks: number
  subjects: SubjectItem[]
}

export default function StepPoolPreview({ state, updateState }: Props) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [collapsedPrimaries, setCollapsedPrimaries] = useState<Record<string, boolean>>({})
  const [collapsedSubjects, setCollapsedSubjects] = useState<Record<string, boolean>>({})
  const [collapsedChapters, setCollapsedChapters] = useState<Record<string, boolean>>({})
  const [previewGroupMode, setPreviewGroupMode] = useState<'by_section' | 'by_subject'>('by_section')

  const hasSectionsInPool = useMemo(() => {
    return state.selectedQuestions.some(q => q.section && q.section.name)
  }, [state.selectedQuestions])

  const isBySection = previewGroupMode === 'by_section' && hasSectionsInPool

  const fetchAutoSelection = useCallback(async () => {
    setLoading(true)
    setError(null)
    setWarnings([])

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const res = await fetch('/admin/test_generator/preview_selection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          language: state.language,
          exam_id: state.exam_id,
          syllabus_ids: state.selectedSyllabiIds,
          subject_ids: state.filterSubjectIds,
          chapter_ids: state.filterChapterIds,
          section_ids: state.filterSectionIds,
          standard_ids: state.filterStandardIds,
          difficulties: state.filterDifficulties,
          question_types: state.filterQuestionTypes,
          include_tags: state.includeTags,
          exclude_tags: state.excludeTags,
          exclude_test_ids: state.excludeTestIds,
          flags: {
            pyq_only: state.pyqOnly,
            exemplar_only: state.exemplarOnly
          },
          weightage_mode: state.weightageMode,
          total_questions: state.totalQuestions,
          total_marks: state.totalMarks,
          positive_marks: state.markingScheme.positive_marks,
          negative_marks: state.markingScheme.negative_marks,
          negative_marking_enabled: state.markingScheme.negative_marking_enabled ?? ((state.markingScheme.negative_marks || 0) > 0),
          section_marking_rules: state.markingScheme.section_marking_rules,
          sections: (state.blueprintSections || []).filter((bs: any) => bs._selected !== false),
          subject_weightages: state.subjectWeightages,
          difficulty_mix: state.difficultyMix
        })
      })

      const contentType = res.headers.get('content-type') || ''
      if (!contentType.includes('application/json')) {
        const text = await res.text()
        console.error('Server returned non-JSON response:', text)
        setError(`Server returned non-JSON response (${res.status}). Please check weightage settings in Step 5 (Weightage).`)
        return
      }

      const data = await res.json()
      if (!res.ok || !data.valid) {
        setError(data.errors?.join(', ') || 'Failed to auto-select questions from pool.')
      } else {
        updateState({ selectedQuestions: data.questions || [] })
        if (data.warnings?.length) setWarnings(data.warnings)
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while running pool selection.')
    } finally {
      setLoading(false)
    }
  }, [
    state.language, state.exam_id, state.selectedSyllabiIds, state.filterSubjectIds,
    state.filterChapterIds, state.filterSectionIds, state.filterStandardIds,
    state.filterDifficulties, state.filterQuestionTypes, state.includeTags,
    state.excludeTags, state.excludeTestIds, state.pyqOnly, state.exemplarOnly,
    state.weightageMode, state.totalQuestions, state.totalMarks, state.markingScheme,
    state.blueprintSections, state.subjectWeightages, state.difficultyMix
  ])

  // Auto fetch selection on initial load if none exists
  useEffect(() => {
    if (state.selectedQuestions.length === 0) {
      fetchAutoSelection()
    }
  }, [])

  const handleRemoveQuestion = (questionId: number) => {
    const updated = state.selectedQuestions.filter(q => q.id !== questionId)
    updateState({ selectedQuestions: updated })
  }

  // Group questions hierarchically: Section -> Subject -> Chapter -> Questions (or Subject -> Chapter -> Questions)
  const groupedQuestions = useMemo(() => {
    const isBySection = previewGroupMode === 'by_section' && hasSectionsInPool

    if (!isBySection) {
      // Direct Subject -> Chapter -> Questions hierarchy preserving subjectWeightages order
      const subMap = new Map<string, {
        id?: number
        name: string
        chaptersMap: Map<string, {
          chapterId?: number
          chapterName: string
          questions: QuestionItem[]
        }>
      }>()

      // Seed subjects according to state.subjectWeightages order
      state.subjectWeightages.forEach(sw => {
        subMap.set(sw.name, {
          id: sw.subject_id,
          name: sw.name,
          chaptersMap: new Map()
        })
      })

      state.selectedQuestions.forEach((q, idx) => {
        const subName = q.subject?.name || 'General Subject'
        const subId = q.subject?.id
        const chapName = q.chapter?.name || 'General Topics'
        const chapId = q.chapter?.id

        if (!subMap.has(subName)) {
          subMap.set(subName, {
            id: subId,
            name: subName,
            chaptersMap: new Map()
          })
        }

        const sEntry = subMap.get(subName)!
        if (!sEntry.chaptersMap.has(chapName)) {
          sEntry.chaptersMap.set(chapName, {
            chapterId: chapId,
            chapterName: chapName,
            questions: []
          })
        }

        sEntry.chaptersMap.get(chapName)!.questions.push({
          question: q,
          globalIndex: idx + 1
        })
      })

      const result: PrimaryGroup[] = []
      subMap.forEach((sVal) => {
        const chapters: ChapterGroup[] = []
        let sTotalQ = 0
        let sTotalM = 0

        sVal.chaptersMap.forEach((cVal) => {
          const cQuestions = cVal.questions
          const cTotalQ = cQuestions.length
          const cTotalM = cQuestions.reduce((sum, item) => sum + (Number(item.question.positive_marks) || Number(state.markingScheme.positive_marks) || 1), 0)

          sTotalQ += cTotalQ
          sTotalM += cTotalM

          chapters.push({
            chapterId: cVal.chapterId,
            chapterName: cVal.chapterName,
            totalQuestions: cTotalQ,
            totalMarks: cTotalM,
            questions: cQuestions
          })
        })

        if (sTotalQ > 0) {
          result.push({
            id: sVal.id,
            name: sVal.name,
            totalQuestions: sTotalQ,
            totalMarks: sTotalM,
            subjects: [{
              subjectId: sVal.id,
              subjectName: sVal.name,
              totalQuestions: sTotalQ,
              totalMarks: sTotalM,
              chapters: chapters
            }]
          })
        }
      })

      return result
    }

    // Structure for By Section: Section -> Subject -> Chapter -> Questions (preserving blueprintSections & subjectWeightages order)
    const primaryMap = new Map<string, {
      id?: number
      name: string
      subjectsMap: Map<string, {
        id?: number
        name: string
        chaptersMap: Map<string, {
          chapterId?: number
          chapterName: string
          questions: QuestionItem[]
        }>
      }>
    }>()

    // Seed sections in exact blueprintSections order
    const activeSections = (state.blueprintSections || []).filter((bs: any) => bs._selected !== false)
    activeSections.forEach(sec => {
      primaryMap.set(sec.name, {
        id: sec.id,
        name: sec.name,
        subjectsMap: new Map()
      })
    })

    state.selectedQuestions.forEach((q, idx) => {
      const primaryName = q.section?.name || 'General Section'
      const primaryId = q.section?.id

      const subjectName = q.subject?.name || 'General Subject'
      const subjectId = q.subject?.id

      const chapName = q.chapter?.name || 'General Topics'
      const chapId = q.chapter?.id

      if (!primaryMap.has(primaryName)) {
        primaryMap.set(primaryName, {
          id: primaryId,
          name: primaryName,
          subjectsMap: new Map()
        })
      }

      const pEntry = primaryMap.get(primaryName)!
      if (!pEntry.subjectsMap.has(subjectName)) {
        pEntry.subjectsMap.set(subjectName, {
          id: subjectId,
          name: subjectName,
          chaptersMap: new Map()
        })
      }

      const sEntry = pEntry.subjectsMap.get(subjectName)!
      if (!sEntry.chaptersMap.has(chapName)) {
        sEntry.chaptersMap.set(chapName, {
          chapterId: chapId,
          chapterName: chapName,
          questions: []
        })
      }

      sEntry.chaptersMap.get(chapName)!.questions.push({
        question: q,
        globalIndex: idx + 1
      })
    })

    const result: PrimaryGroup[] = []
    primaryMap.forEach((pVal) => {
      const subjects: SubjectItem[] = []
      let pTotalQ = 0
      let pTotalM = 0

      pVal.subjectsMap.forEach((sVal) => {
        const chapters: ChapterGroup[] = []
        let sTotalQ = 0
        let sTotalM = 0

        sVal.chaptersMap.forEach((cVal) => {
          const cQuestions = cVal.questions
          const cTotalQ = cQuestions.length
          const cTotalM = cQuestions.reduce((sum, item) => sum + (Number(item.question.positive_marks) || Number(state.markingScheme.positive_marks) || 1), 0)

          sTotalQ += cTotalQ
          sTotalM += cTotalM

          chapters.push({
            chapterId: cVal.chapterId,
            chapterName: cVal.chapterName,
            totalQuestions: cTotalQ,
            totalMarks: cTotalM,
            questions: cQuestions
          })
        })

        pTotalQ += sTotalQ
        pTotalM += sTotalM

        if (sTotalQ > 0) {
          subjects.push({
            subjectId: sVal.id,
            subjectName: sVal.name,
            totalQuestions: sTotalQ,
            totalMarks: sTotalM,
            chapters: chapters
          })
        }
      })

      if (pTotalQ > 0) {
        result.push({
          id: pVal.id,
          name: pVal.name,
          totalQuestions: pTotalQ,
          totalMarks: pTotalM,
          subjects: subjects
        })
      }
    })

    return result
  }, [state.selectedQuestions, previewGroupMode, hasSectionsInPool, state.markingScheme.positive_marks])

  const togglePrimary = (name: string) => {
    setCollapsedPrimaries(prev => ({ ...prev, [name]: !prev[name] }))
  }

  const toggleSubject = (key: string) => {
    setCollapsedSubjects(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleChapter = (key: string) => {
    setCollapsedChapters(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleAll = () => {
    const hasAnyCollapsed = Object.values(collapsedPrimaries).some(Boolean) ||
      Object.values(collapsedSubjects).some(Boolean) ||
      Object.values(collapsedChapters).some(Boolean)

    if (hasAnyCollapsed) {
      setCollapsedPrimaries({})
      setCollapsedSubjects({})
      setCollapsedChapters({})
    } else {
      const allPrim: Record<string, boolean> = {}
      const allSubs: Record<string, boolean> = {}
      const allChaps: Record<string, boolean> = {}

      groupedQuestions.forEach(prim => {
        allPrim[prim.name] = true
        prim.subjects.forEach(sub => {
          allSubs[`${prim.name}-${sub.subjectName}`] = true
          sub.chapters.forEach(ch => {
            allChaps[`${prim.name}-${sub.subjectName}-${ch.chapterName}`] = true
          })
        })
      })

      setCollapsedPrimaries(allPrim)
      setCollapsedSubjects(allSubs)
      setCollapsedChapters(allChaps)
    }
  }

  const selectedCount = state.selectedQuestions.length
  const totalSelectedMarks = state.selectedQuestions.reduce((sum, q) => sum + (Number(q.positive_marks) || Number(state.markingScheme.positive_marks) || 1), 0)

  const subjectSelectedCount: Record<string, number> = {}
  state.selectedQuestions.forEach(q => {
    const subName = q.subject?.name || 'Unassigned'
    subjectSelectedCount[subName] = (subjectSelectedCount[subName] || 0) + 1
  })

  const difficultySelectedCount: Record<string, number> = {}
  state.selectedQuestions.forEach(q => {
    const diff = q.difficulty || 'Medium'
    difficultySelectedCount[diff] = (difficultySelectedCount[diff] || 0) + 1
  })

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3 sm:pb-4">
        <div>
          <h2 className="text-base sm:text-lg font-bold flex items-center gap-2 text-foreground">
            <Eye className="h-4 sm:h-5 w-4 sm:w-5 text-primary shrink-0" /> Question Pool Selection & Preview
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Review hierarchical question distribution across subjects, chapters, and preview individual questions
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={fetchAutoSelection}
          disabled={loading}
          className="text-xs font-semibold gap-1.5 border-border/80 bg-card hover:bg-muted self-stretch sm:self-auto rounded-xl h-8 sm:h-9"
        >
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              <span>Selecting...</span>
            </>
          ) : (
            <>
              <RefreshCw className="h-3.5 w-3.5 text-primary" />
              <span>Re-run Auto Selection</span>
            </>
          )}
        </Button>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-destructive/10 border border-destructive/30 text-destructive text-xs">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <strong className="block font-bold">Selection Error</strong>
            <p>{error}</p>
          </div>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="flex items-start gap-3 p-3.5 rounded-2xl bg-primary/10 border border-primary/20 text-primary text-xs">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <strong className="block font-bold">Pool Notices</strong>
            <ul className="list-disc list-inside space-y-0.5">
              {warnings.map((w, idx) => (
                <li key={idx}>{w}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {/* Controls Bar: Selected questions label, Group mode pill, and action buttons in unified responsive toolbar */}
          <div className="flex items-center justify-between gap-1.5 p-2 sm:p-3 bg-muted/20 border border-border/70 rounded-2xl">
            <div className="flex items-center gap-1 shrink-0">
              <Label className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-foreground">
                <span className="sm:hidden">Selected</span>
                <span className="hidden sm:inline">Selected Questions</span>
              </Label>
              <Badge variant="outline" className="text-[9px] sm:text-[10px] font-bold bg-primary/10 text-primary border-primary/20 px-1 py-0">
                {selectedCount}
              </Badge>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {hasSectionsInPool && (
                <div className="flex items-center gap-0.5 border border-border/80 rounded-lg p-0.5 bg-card shrink-0">
                  <button
                    type="button"
                    onClick={() => setPreviewGroupMode('by_section')}
                    className={`px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[11px] font-semibold rounded transition-colors ${
                      previewGroupMode === 'by_section'
                        ? 'bg-primary text-primary-foreground shadow-2xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Section
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewGroupMode('by_subject')}
                    className={`px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[11px] font-semibold rounded transition-colors ${
                      previewGroupMode === 'by_subject'
                        ? 'bg-primary text-primary-foreground shadow-2xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Subject
                  </button>
                </div>
              )}

              {selectedCount > 0 && (
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleAll}
                    className="text-[10px] sm:text-[11px] h-6 sm:h-7 px-1.5 sm:px-2 text-muted-foreground hover:text-foreground gap-1 rounded-lg"
                    title="Toggle all collapsed/expanded"
                  >
                    <ChevronsUpDown className="h-3 w-3" />
                    <span className="hidden sm:inline">Toggle All</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateState({ selectedQuestions: [] })}
                    className="text-[10px] sm:text-[11px] h-6 sm:h-7 px-1.5 sm:px-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 gap-1 rounded-lg"
                    title="Clear selected questions"
                  >
                    <Trash2 className="h-3 w-3" />
                    <span className="hidden sm:inline">Clear</span>
                  </Button>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground gap-3 bg-card border border-border/80 rounded-2xl shadow-glass-xs">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-xs font-medium">Running weighted selection algorithm...</span>
            </div>
          ) : selectedCount === 0 ? (
            <div className="text-center py-16 px-4 bg-card border border-border/80 rounded-2xl shadow-glass-xs space-y-3">
              <Sparkles className="h-8 w-8 text-primary mx-auto opacity-50" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">No questions selected yet</p>
                <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                  Click "Re-run Auto Selection" to generate an optimized question paper draft.
                </p>
              </div>
              <Button size="sm" onClick={fetchAutoSelection} className="text-xs gap-1.5 font-bold rounded-xl">
                <Sparkles className="h-3.5 w-3.5" /> Auto-Select Questions
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {groupedQuestions.map(primary => {
                const isPrimaryCollapsed = collapsedPrimaries[primary.name]

                return (
                  <div
                    key={primary.name}
                    className="border border-border/80 rounded-2xl bg-card shadow-glass-xs overflow-hidden transition-all"
                  >
                    <div
                      onClick={() => togglePrimary(primary.name)}
                      className="p-3.5 bg-muted/40 hover:bg-muted/60 cursor-pointer flex items-center justify-between gap-3 border-b border-border/60 transition-colors select-none"
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="h-7 w-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                          <BookOpen className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-xs sm:text-sm font-bold text-foreground block truncate">
                            {primary.name}
                          </span>
                          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-0.5">
                            <span>
                              {isBySection
                                ? `${primary.subjects.length} ${primary.subjects.length === 1 ? 'Subject' : 'Subjects'}`
                                : `${primary.subjects[0]?.chapters.length || 0} ${(primary.subjects[0]?.chapters.length || 0) === 1 ? 'Chapter' : 'Chapters'}`}
                            </span>
                            <span className="text-muted-foreground/60">•</span>
                            <span className="font-semibold text-foreground">
                              {primary.totalQuestions} Qs
                            </span>
                            <span className="text-muted-foreground/60">•</span>
                            <span className="font-extrabold text-primary">
                              {primary.totalMarks} Marks
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        <div className="text-muted-foreground p-1">
                          {isPrimaryCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </div>
                    </div>
                    {/* Card Content: Either Direct Chapters (by_subject mode) OR Nested Subject cards (by_section mode) */}
                    {!isPrimaryCollapsed && (
                      <div className="p-2.5 sm:p-3.5 space-y-3 bg-card">
                        {!isBySection ? (
                          /* Direct Chapters List inside Subject */
                          primary.subjects[0]?.chapters.map(chapter => {
                            const chapKey = `${primary.name}-${chapter.chapterName}`
                            const isChapCollapsed = collapsedChapters[chapKey]

                            return (
                              <div
                                key={chapter.chapterName}
                                className="border border-border/70 rounded-xl overflow-hidden bg-background"
                              >
                                <div
                                  onClick={() => toggleChapter(chapKey)}
                                  className="px-2.5 sm:px-3.5 py-2 sm:py-2.5 bg-muted/20 hover:bg-muted/40 cursor-pointer flex items-center justify-between gap-2 border-b border-border/40 transition-colors select-none"
                                >
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <Folder className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <span className="text-xs font-semibold text-foreground block truncate">
                                        {chapter.chapterName}
                                      </span>
                                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-0.5">
                                        <span className="font-semibold text-foreground">
                                          {chapter.questions.length} Qs
                                        </span>
                                        <span className="text-muted-foreground/60">•</span>
                                        <span className="font-extrabold text-primary">
                                          {chapter.totalMarks} Marks
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <div className="text-muted-foreground p-0.5">
                                      {isChapCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                    </div>
                                  </div>
                                </div>

                                {!isChapCollapsed && (
                                  <div>
                                    {/* Mobile Cards View (md:hidden) */}
                                    <div className="md:hidden p-2 space-y-2">
                                      {chapter.questions.map(({ question: q, globalIndex }) => (
                                        <div
                                          key={q.id || globalIndex}
                                          className="p-2.5 rounded-xl bg-card border border-border/70 space-y-2 shadow-2xs"
                                        >
                                          {/* Full Width Question Text at Top */}
                                          <a
                                            href={`/admin/questions/${q.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group/link block hover:text-primary transition-colors cursor-pointer"
                                            title="Open question in Question Bank"
                                          >
                                            <KatexRenderer
                                              content={q.question_text_preview || 'No preview text'}
                                              className="line-clamp-2 text-foreground group-hover/link:text-primary font-medium text-[11px] leading-snug"
                                            />
                                          </a>

                                          {/* Footer with Q Number, Badges, Difficulty, and Delete Button */}
                                          <div className="flex items-center justify-between gap-1.5 pt-1.5 border-t border-border/40 text-[10px]">
                                            <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
                                              <span className="text-[10px] font-extrabold text-muted-foreground bg-muted/60 rounded-md px-1.5 py-0.5 shrink-0">
                                                #{globalIndex}
                                              </span>
                                              {hasSectionsInPool && q.section && (
                                                <Badge variant="outline" className="text-[9px] font-semibold px-1.5 py-0 h-4 bg-primary/10 text-primary border-primary/20 max-w-[100px] truncate">
                                                  {q.section.name}
                                                </Badge>
                                              )}
                                              <Badge variant="outline" className="text-[9px] uppercase font-semibold px-1.5 py-0 h-4 bg-muted/60 text-foreground border-border">
                                                {q.question_type ? q.question_type.replace('_', ' ') : 'mcq'}
                                              </Badge>
                                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
                                                q.difficulty === 'Easy'
                                                  ? 'bg-secondary/15 text-secondary border border-secondary/30'
                                                  : q.difficulty === 'Hard'
                                                  ? 'bg-destructive/10 text-destructive border border-destructive/20'
                                                  : 'bg-primary/10 text-primary border border-primary/20'
                                              }`}>
                                                {q.difficulty || 'Medium'}
                                              </span>
                                            </div>

                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleRemoveQuestion(q.id)}
                                              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md shrink-0"
                                              title="Remove this question"
                                            >
                                              <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    {/* Desktop Table View (hidden md:table) */}
                                    <div className="hidden md:block overflow-x-auto">
                                      <table className="w-full text-xs text-left">
                                        <thead className="bg-muted/30 border-b border-border/40 text-muted-foreground uppercase text-[9px] font-bold tracking-wider">
                                          <tr>
                                            <th className="py-2 px-3 w-10 text-center">#</th>
                                            <th className="py-2 px-3">Question Text</th>
                                            {hasSectionsInPool && (
                                              <th className="py-2 px-3 text-center w-24">Section</th>
                                            )}
                                            <th className="py-2 px-3 text-center w-20">Type</th>
                                            <th className="py-2 px-3 text-center w-20">Difficulty</th>
                                            <th className="py-2 px-3 text-center w-10">Action</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/40">
                                          {chapter.questions.map(({ question: q, globalIndex }) => (
                                            <tr key={q.id || globalIndex} className="hover:bg-muted/20 transition-colors">
                                              <td className="py-2.5 px-3 font-bold text-muted-foreground text-center">
                                                {globalIndex}
                                              </td>

                                              <td className="py-2.5 px-3">
                                                <a
                                                  href={`/admin/questions/${q.id}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="group/link inline-flex items-start gap-1.5 hover:text-primary transition-colors cursor-pointer"
                                                  title="Open question in Question Bank"
                                                >
                                                  <KatexRenderer
                                                    content={q.question_text_preview || 'No preview text'}
                                                    className="line-clamp-2 text-foreground group-hover/link:text-primary font-medium text-[11px]"
                                                  />
                                                  <ExternalLink className="h-3 w-3 text-muted-foreground group-hover/link:text-primary shrink-0 mt-0.5 opacity-60 group-hover/link:opacity-100 transition-opacity" />
                                                </a>
                                              </td>

                                              {hasSectionsInPool && (
                                                <td className="py-2.5 px-3 text-center">
                                                  {q.section ? (
                                                    <Badge variant="outline" className="text-[9px] font-semibold px-1.5 py-0 h-4 bg-primary/10 text-primary border-primary/20 max-w-[120px] truncate">
                                                      {q.section.name}
                                                    </Badge>
                                                  ) : (
                                                    <span className="text-[10px] text-muted-foreground">—</span>
                                                  )}
                                                </td>
                                              )}

                                              <td className="py-2.5 px-3 text-center">
                                                <Badge variant="outline" className="text-[9px] uppercase font-semibold px-1.5 py-0 h-4 bg-muted/60 text-foreground border-border">
                                                  {q.question_type ? q.question_type.replace('_', ' ') : 'mcq'}
                                                </Badge>
                                              </td>

                                              <td className="py-2.5 px-3 text-center">
                                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                                  q.difficulty === 'Easy'
                                                    ? 'bg-secondary/15 text-secondary border border-secondary/30'
                                                    : q.difficulty === 'Hard'
                                                    ? 'bg-destructive/10 text-destructive border border-destructive/20'
                                                    : 'bg-primary/10 text-primary border border-primary/20'
                                                }`}>
                                                  {q.difficulty || 'Medium'}
                                                </span>
                                              </td>

                                              <td className="py-2.5 px-3 text-center">
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => handleRemoveQuestion(q.id)}
                                                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md"
                                                  title="Remove this question from selection"
                                                >
                                                  <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })
                        ) : (
                          /* Nested Subject cards inside Section */
                          primary.subjects.map(subject => {
                            const subKey = `${primary.name}-${subject.subjectName}`
                            const isSubCollapsed = collapsedSubjects[subKey]

                            return (
                              <div
                                key={subject.subjectName}
                                className="border border-border/70 rounded-xl overflow-hidden bg-muted/10 space-y-2"
                              >
                                <div
                                  onClick={() => toggleSubject(subKey)}
                                  className="p-2 sm:p-2.5 bg-muted/30 hover:bg-muted/50 cursor-pointer flex items-center justify-between gap-2 border-b border-border/50 transition-colors select-none"
                                >
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <span className="text-xs font-bold text-foreground block truncate">
                                        {subject.subjectName}
                                      </span>
                                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-0.5">
                                        <span>
                                          {subject.chapters.length} {subject.chapters.length === 1 ? 'Chapter' : 'Chapters'}
                                        </span>
                                        <span className="text-muted-foreground/60">•</span>
                                        <span className="font-semibold text-foreground">
                                          {subject.totalQuestions} Qs
                                        </span>
                                        <span className="text-muted-foreground/60">•</span>
                                        <span className="font-extrabold text-primary">
                                          {subject.totalMarks} Marks
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <div className="text-muted-foreground p-0.5">
                                      {isSubCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                    </div>
                                  </div>
                                </div>

                                {!isSubCollapsed && (
                                  <div className="p-2 sm:p-2.5 pt-0 space-y-2">
                                    {subject.chapters.map(chapter => {
                                      const chapKey = `${primary.name}-${subject.subjectName}-${chapter.chapterName}`
                                      const isChapCollapsed = collapsedChapters[chapKey]

                                      return (
                                        <div
                                          key={chapter.chapterName}
                                          className="border border-border/70 rounded-lg overflow-hidden bg-background"
                                        >
                                          <div
                                            onClick={() => toggleChapter(chapKey)}
                                            className="px-2.5 sm:px-3 py-1.5 bg-muted/20 hover:bg-muted/40 cursor-pointer flex items-center justify-between gap-1.5 border-b border-border/40 transition-colors select-none"
                                          >
                                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                              <Folder className="h-3 w-3 text-muted-foreground shrink-0" />
                                              <div className="min-w-0 flex-1">
                                                <span className="text-[11px] font-semibold text-foreground block truncate">
                                                  {chapter.chapterName}
                                                </span>
                                                <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground mt-0.5">
                                                  <span className="font-semibold text-foreground">
                                                    {chapter.questions.length} Qs
                                                  </span>
                                                  <span className="text-muted-foreground/60">•</span>
                                                  <span className="font-extrabold text-primary">
                                                    {chapter.totalMarks} Marks
                                                  </span>
                                                </div>
                                              </div>
                                            </div>

                                            <div className="flex items-center gap-1 shrink-0">
                                              <div className="text-muted-foreground p-0.5">
                                                {isChapCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                              </div>
                                            </div>
                                          </div>

                                          {!isChapCollapsed && (
                                            <div>
                                              {/* Mobile Cards View (md:hidden) */}
                                              <div className="md:hidden p-2 space-y-2">
                                                {chapter.questions.map(({ question: q, globalIndex }) => (
                                                  <div
                                                    key={q.id || globalIndex}
                                                    className="p-2.5 rounded-xl bg-card border border-border/70 space-y-2 shadow-2xs"
                                                  >
                                                      {/* Full Width Question Text at Top */}
                                                      <a
                                                        href={`/admin/questions/${q.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="group/link block hover:text-primary transition-colors cursor-pointer"
                                                        title="Open question in Question Bank"
                                                      >
                                                        <KatexRenderer
                                                          content={q.question_text_preview || 'No preview text'}
                                                          className="line-clamp-2 text-foreground group-hover/link:text-primary font-medium text-[11px] leading-snug"
                                                        />
                                                      </a>

                                                      {/* Footer with Q Number, Badges, Difficulty, and Delete Button */}
                                                      <div className="flex items-center justify-between gap-1.5 pt-1.5 border-t border-border/40 text-[10px]">
                                                        <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
                                                          <span className="text-[10px] font-extrabold text-muted-foreground bg-muted/60 rounded-md px-1.5 py-0.5 shrink-0">
                                                            #{globalIndex}
                                                          </span>
                                                          <Badge variant="outline" className="text-[9px] uppercase font-semibold px-1.5 py-0 h-4 bg-muted/60 text-foreground border-border">
                                                            {q.question_type ? q.question_type.replace('_', ' ') : 'mcq'}
                                                          </Badge>
                                                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
                                                            q.difficulty === 'Easy'
                                                              ? 'bg-secondary/15 text-secondary border border-secondary/30'
                                                              : q.difficulty === 'Hard'
                                                              ? 'bg-destructive/10 text-destructive border border-destructive/20'
                                                              : 'bg-primary/10 text-primary border border-primary/20'
                                                          }`}>
                                                            {q.difficulty || 'Medium'}
                                                          </span>
                                                        </div>

                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          onClick={() => handleRemoveQuestion(q.id)}
                                                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md shrink-0"
                                                          title="Remove this question"
                                                        >
                                                          <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                      </div>
                                                  </div>
                                                ))}
                                              </div>

                                              {/* Desktop Table View (hidden md:table) */}
                                              <div className="hidden md:block overflow-x-auto">
                                                <table className="w-full text-xs text-left">
                                                  <thead className="bg-muted/30 border-b border-border/40 text-muted-foreground uppercase text-[9px] font-bold tracking-wider">
                                                    <tr>
                                                      <th className="py-2 px-3 w-10 text-center">#</th>
                                                      <th className="py-2 px-3">Question Text</th>
                                                      <th className="py-2 px-3 text-center w-20">Type</th>
                                                      <th className="py-2 px-3 text-center w-20">Difficulty</th>
                                                      <th className="py-2 px-3 text-center w-10">Action</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="divide-y divide-border/40">
                                                    {chapter.questions.map(({ question: q, globalIndex }) => (
                                                      <tr key={q.id || globalIndex} className="hover:bg-muted/20 transition-colors">
                                                        <td className="py-2.5 px-3 font-bold text-muted-foreground text-center">
                                                          {globalIndex}
                                                        </td>

                                                        <td className="py-2.5 px-3">
                                                          <a
                                                            href={`/admin/questions/${q.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="group/link inline-flex items-start gap-1.5 hover:text-primary transition-colors cursor-pointer"
                                                            title="Open question in Question Bank"
                                                          >
                                                            <KatexRenderer
                                                              content={q.question_text_preview || 'No preview text'}
                                                              className="line-clamp-2 text-foreground group-hover/link:text-primary font-medium text-[11px]"
                                                            />
                                                            <ExternalLink className="h-3 w-3 text-muted-foreground group-hover/link:text-primary shrink-0 mt-0.5 opacity-60 group-hover/link:opacity-100 transition-opacity" />
                                                          </a>
                                                        </td>

                                                        <td className="py-2.5 px-3 text-center">
                                                          <Badge variant="outline" className="text-[9px] uppercase font-semibold px-1.5 py-0 h-4 bg-muted/60 text-foreground border-border">
                                                            {q.question_type ? q.question_type.replace('_', ' ') : 'mcq'}
                                                          </Badge>
                                                        </td>

                                                        <td className="py-2.5 px-3 text-center">
                                                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                                            q.difficulty === 'Easy'
                                                              ? 'bg-secondary/15 text-secondary border border-secondary/30'
                                                              : q.difficulty === 'Hard'
                                                              ? 'bg-destructive/10 text-destructive border border-destructive/20'
                                                              : 'bg-primary/10 text-primary border border-primary/20'
                                                          }`}>
                                                            {q.difficulty || 'Medium'}
                                                          </span>
                                                        </td>

                                                        <td className="py-2.5 px-3 text-center">
                                                          <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemoveQuestion(q.id)}
                                                            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md"
                                                            title="Remove this question from selection"
                                                          >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                          </Button>
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            )
                          })
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Right Column: Live Selection Progress Sidebar */}
        <div>
          <div className="bg-card border border-border/80 rounded-2xl p-5 shadow-glass-xs space-y-5 sticky top-4">
            <div className="flex items-center justify-between border-b border-border/60 pb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <BarChart2 className="h-4 w-4 text-primary" /> Selection Progress
              </span>
              <Badge
                variant="outline"
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  selectedCount === state.totalQuestions
                    ? 'bg-secondary/15 text-secondary border-secondary/30'
                    : 'bg-primary/10 text-primary border-primary/20'
                }`}
              >
                {selectedCount === state.totalQuestions ? 'Target Met' : `${selectedCount}/${state.totalQuestions} Qs`}
              </Badge>
            </div>

            {/* Quick Metrics 2-Up Tiles */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-muted/20 border border-border/60 space-y-1">
                <span className="text-[10px] font-semibold text-muted-foreground block uppercase tracking-wider">
                  Questions
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-extrabold text-foreground">{selectedCount}</span>
                  <span className="text-xs text-muted-foreground font-medium">/ {state.totalQuestions}</span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-1.5">
                  <div
                    className={`h-full transition-all duration-300 ${
                      selectedCount === state.totalQuestions ? 'bg-secondary' : 'bg-primary'
                    }`}
                    style={{ width: `${Math.min(100, Math.round((selectedCount / (state.totalQuestions || 1)) * 100))}%` }}
                  />
                </div>
              </div>

              <div className="p-3 rounded-xl bg-muted/20 border border-border/60 space-y-1">
                <span className="text-[10px] font-semibold text-muted-foreground block uppercase tracking-wider">
                  Total Marks
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-extrabold text-foreground">{totalSelectedMarks}</span>
                  <span className="text-xs text-muted-foreground font-medium">/ {state.totalMarks}</span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-1.5">
                  <div
                    className={`h-full transition-all duration-300 ${
                      totalSelectedMarks === state.totalMarks ? 'bg-secondary' : 'bg-primary'
                    }`}
                    style={{ width: `${Math.min(100, Math.round((totalSelectedMarks / (state.totalMarks || 1)) * 100))}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Section Distribution Progress */}
            {hasSectionsInPool && (() => {
              const activeSections = (state.blueprintSections || []).filter((bs: any) => bs._selected !== false)
              if (activeSections.length === 0) return null

              const sectionCountMap: Record<string, number> = {}
              state.selectedQuestions.forEach(q => {
                const sName = q.section?.name || 'General Section'
                sectionCountMap[sName] = (sectionCountMap[sName] || 0) + 1
              })

              return (
                <div className="space-y-3 pt-3 border-t border-border/60">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                    Paper Sections
                  </span>

                  <div className="space-y-3">
                    {activeSections.map((sec: any) => {
                      const currentCount = sectionCountMap[sec.name] || 0
                      const targetCount = sec.total_questions || 0
                      const isComplete = currentCount === targetCount && targetCount > 0
                      const isOver = currentCount > targetCount
                      const isUnder = currentCount < targetCount

                      // Max scale represents either 100% or whatever is higher if over target (up to 150%)
                      const maxScale = Math.max(targetCount, currentCount, 1)
                      const targetTickPercent = targetCount > 0 ? (targetCount / maxScale) * 100 : 100
                      const fillPercent = (currentCount / maxScale) * 100

                      return (
                        <div key={sec.id} className="p-2.5 rounded-xl bg-muted/15 border border-border/60 text-xs space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-bold text-foreground truncate max-w-[140px]" title={sec.name}>
                              {sec.name}
                            </span>
                            <div className="flex items-center gap-1.5">
                              {isOver && (
                                <Badge variant="outline" className="text-[9px] font-extrabold px-1.5 py-0 h-4 bg-destructive/10 text-destructive border-destructive/30">
                                  +{currentCount - targetCount} Over
                                </Badge>
                              )}
                              {isUnder && (
                                <Badge variant="outline" className="text-[9px] font-bold px-1.5 py-0 h-4 bg-muted text-muted-foreground border-border">
                                  {targetCount - currentCount} Needed
                                </Badge>
                              )}
                              {isComplete && (
                                <Badge variant="outline" className="text-[9px] font-extrabold px-1.5 py-0 h-4 bg-secondary/15 text-secondary border-secondary/30">
                                  Target Met
                                </Badge>
                              )}
                              <span className={`text-[11px] font-bold ${
                                isComplete ? 'text-secondary' : isOver ? 'text-destructive' : 'text-muted-foreground'
                              }`}>
                                {currentCount} / {targetCount} Qs
                              </span>
                            </div>
                          </div>

                          {/* Progress Bar with Target Indicator Tick and Over/Under Gradients */}
                          <div className="relative h-2 w-full bg-muted/60 rounded-full overflow-hidden">
                            {/* Bar Fill */}
                            <div
                              className={`h-full transition-all duration-300 rounded-full ${
                                isComplete
                                  ? 'bg-secondary'
                                  : isOver
                                  ? 'bg-destructive'
                                  : 'bg-muted-foreground/40'
                              }`}
                              style={{ width: `${Math.min(100, fillPercent)}%` }}
                            />
                            {/* Target Marker Tick */}
                            {isOver && (
                              <div
                                className="absolute top-0 bottom-0 w-0.5 bg-foreground/80 z-10"
                                style={{ left: `${targetTickPercent}%` }}
                                title={`Blueprint target: ${targetCount} Qs`}
                              />
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })()}

            {/* Subject Distribution Progress */}
            <div className="space-y-3 pt-3 border-t border-border/60">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                Subject Breakdown
              </span>

              <div className="space-y-3">
                {state.subjectWeightages.map(sw => {
                  const currentSubCount = subjectSelectedCount[sw.name] || 0
                  const targetSubCount = sw.calculated_questions || 0
                  const isComplete = currentSubCount === targetSubCount && targetSubCount > 0
                  const isOver = currentSubCount > targetSubCount
                  const isUnder = currentSubCount < targetSubCount

                  const maxScale = Math.max(targetSubCount, currentSubCount, 1)
                  const targetTickPercent = targetSubCount > 0 ? (targetSubCount / maxScale) * 100 : 100
                  const fillPercent = (currentSubCount / maxScale) * 100

                  return (
                    <div key={sw.subject_id} className="p-2.5 rounded-xl bg-muted/15 border border-border/60 text-xs space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-foreground truncate max-w-[140px]" title={sw.name}>
                          {sw.name}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {isOver && (
                            <Badge variant="outline" className="text-[9px] font-extrabold px-1.5 py-0 h-4 bg-destructive/10 text-destructive border-destructive/30">
                              +{currentSubCount - targetSubCount} Over
                            </Badge>
                          )}
                          {isUnder && (
                            <Badge variant="outline" className="text-[9px] font-bold px-1.5 py-0 h-4 bg-muted text-muted-foreground border-border">
                              {targetSubCount - currentSubCount} Needed
                            </Badge>
                          )}
                          {isComplete && (
                            <Badge variant="outline" className="text-[9px] font-extrabold px-1.5 py-0 h-4 bg-secondary/15 text-secondary border-secondary/30">
                              Target Met
                            </Badge>
                          )}
                          <span className={`text-[11px] font-bold ${
                            isComplete ? 'text-secondary' : isOver ? 'text-destructive' : 'text-muted-foreground'
                          }`}>
                            {currentSubCount} / {targetSubCount} Qs
                          </span>
                        </div>
                      </div>

                      {/* Progress Bar with Target Indicator */}
                      <div className="relative h-2 w-full bg-muted/60 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 rounded-full ${
                            isComplete
                              ? 'bg-secondary'
                              : isOver
                              ? 'bg-destructive'
                              : 'bg-muted-foreground/40'
                          }`}
                          style={{ width: `${Math.min(100, fillPercent)}%` }}
                        />
                        {isOver && (
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-foreground/80 z-10"
                            style={{ left: `${targetTickPercent}%` }}
                            title={`Target: ${targetSubCount} Qs`}
                          />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Difficulty Breakdown */}
            <div className="space-y-2.5 pt-3 border-t border-border/60">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                Difficulty Mix
              </span>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                {[
                  { label: 'Easy', count: difficultySelectedCount['Easy'] || 0, color: 'text-secondary bg-secondary/10 border-secondary/20' },
                  { label: 'Medium', count: difficultySelectedCount['Medium'] || 0, color: 'text-primary bg-primary/10 border-primary/20' },
                  { label: 'Hard', count: difficultySelectedCount['Hard'] || 0, color: 'text-destructive bg-destructive/10 border-destructive/20' }
                ].map(d => (
                  <div key={d.label} className={`p-2.5 rounded-xl border ${d.color}`}>
                    <div className="text-base font-extrabold">{d.count}</div>
                    <div className="text-[10px] font-semibold opacity-80">{d.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
